import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

import type { DeviceChannels, Unit } from "../view-model";

interface UnitStore {
  // State
  units: { [id: string]: Unit };
  loadingStates: {
    units: boolean;
  };
  errors: {
    units?: string;
  };
  lastUpdated: {
    units: number;
  };

  // Actions
  setUnits: (devices: Unit[]) => void;
  addUnits: (units: Unit[]) => void;
  updateUnit: (deviceId: string, updates: Partial<Unit>) => void;
  updateUnitChannel: (deviceId: string, channelId: string, value: any) => void;
  updateUnitChannels: (deviceId: string, channels: DeviceChannels) => void;
  setLoading: (key: keyof UnitStore["loadingStates"], loading: boolean) => void;
  setError: (key: keyof UnitStore["errors"], error: string | null) => void;
  clearErrors: () => void;

  // Selectors
  getUnit: (id: string) => Unit | undefined;
  getUnits: () => Unit[];
  getUnitsForDevice: (deviceId: string) => Unit[];
}

export const useUnitStore = create<UnitStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      units: {},
      loadingStates: {
        units: false,
      },
      errors: {},
      lastUpdated: {
        units: 0,
      },

      // Actions
      setUnits: (devices: Unit[]) => {
        const unitMap: { [id: string]: Unit } = {};
        for (const device of devices) {
          unitMap[device.id] = device;
        }

        set({
          units: unitMap,
          lastUpdated: { ...get().lastUpdated, units: Date.now() },
          loadingStates: { ...get().loadingStates, units: false },
          errors: { ...get().errors, units: undefined },
        });
      },

      addUnits: (units: Unit[]) => {
        const unitMap = get().units;
        for (const unit of units) {
          unitMap[unit.id] = unit;
        }

        set({
          units: unitMap,
          lastUpdated: { ...get().lastUpdated, units: Date.now() },
          loadingStates: { ...get().loadingStates, units: false },
          errors: { ...get().errors, units: undefined },
        });
      },

      updateUnit: (unitId: string, updates: Partial<Unit>) => {
        set((state) => ({
          units: {
            ...state.units,
            [unitId]: {
              ...state.units[unitId],
              ...updates,
            },
          },
        }));
      },

      updateUnitChannel: (unitId: string, channelId: string, value: any) => {
        set((state) => {
          const unit = state.units[unitId];
          if (!unit) return state;

          const updatedChannels = { ...unit.functions };
          for (const key of Object.keys(updatedChannels)) {
            const channel = updatedChannels[key];
            if (channel?.id === channelId) {
              updatedChannels[key] = {
                ...channel,
                current: { value, timestamp: new Date() },
              };
            }
          }

          return {
            units: {
              ...state.units,
              [unitId]: {
                ...unit,
                channels: updatedChannels,
              },
            },
            lastUpdated: { ...state.lastUpdated, operationState: Date.now() },
          };
        });
      },

      updateUnitChannels: (unitId: string, channels: DeviceChannels) => {
        set((state) => {
          const unit = state.units[unitId];
          if (!unit) return state;

          return {
            units: {
              ...state.units,
              [unitId]: {
                ...unit,
                channels,
              },
            },
            lastUpdated: { ...state.lastUpdated, operationState: Date.now() },
          };
        });
      },

      setLoading: (key: keyof UnitStore["loadingStates"], loading: boolean) => {
        set((state) => ({
          loadingStates: { ...state.loadingStates, [key]: loading },
        }));
      },

      setError: (key: keyof UnitStore["errors"], error: string | null) => {
        set((state) => ({
          errors: { ...state.errors, [key]: error || undefined },
        }));
      },

      clearErrors: () => {
        set({ errors: {} });
      },

      // Selectors
      getUnit: (id: string): Unit => get().units[id],

      getUnits: (): Unit[] => Object.values(get().units),

      /*
       * use this if id is set to browse path!
       * Object.entries(get().units)
          // match on prefix of the id (browsePath)
          .filter(([id, _unit]) => id.startsWith(deviceId))
          .map(([_id, unit]) => unit),
       */
      getUnitsForDevice: (deviceId: string): Unit[] =>
        Object.values(get().units).filter(
          (unit: Unit) => unit.deviceId === deviceId
        ),

      getUnitStatus: (unitId: string) => {
        const unit = get().units[unitId];
        if (!unit) return undefined;

        return unit.state;
      },
    })),
    {
      name: "units-store", // Name that will appear in Redux DevTools
    }
  )
);
